// ============================================
// pages/society/SocietyDashboard.tsx — /society/consignments
// PO-centric view of bags supplied to this PACS. Each PO card shows the lot's
// commodity / bag type / dispatched / received, then the consignment(s) where
// the society records bags utilized + returned. No transfer or supplier-payment
// columns — those belong to DM and Markfed-internal workflows respectively.
// ============================================
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Package, AlertCircle, CheckCircle, RotateCcw, Hash, ChevronDown, ChevronRight,
  FileText, Pencil, Loader2, XCircle, ShieldCheck,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { gunnyUtilizationAPI, seasonsAPI, commoditiesAPI } from '../../api/services';
import { bagTypeLabel } from '../../components/gunny/bagTypes';
import { Pagination } from '../../components/common/Pagination/Pagination';
import { usePagination } from '../../components/common/Pagination/usePagination';

interface Season { id: number; season_name: string; is_active: boolean }
interface Consignment {
  id: number;
  status: string;
  bags_supplied: number;
  bags_utilized: number;
  bags_returned: number;
  receipt_verified_at: string | null;
  receipt_rejected_at: string | null;
  receipt_rejection_reason: string | null;
  rejection_reason: string | null;
}
interface LineItem {
  po_item_id: number;
  pacs_id: number | null;
  pacs_name: string | null;
  bag_type: string;
  specification: string | null;
  rate: number;
  commodity_id: number | null;
  commodity_name: string | null;
  delivery_godown_name: string | null;
  ordered: number;
  dispatched: number;
  received: number;
  pending_ack: number;
  remaining: number;
  consignments: Consignment[];
}
interface PoCard {
  id: number;
  season_id: number;
  po_number: string;
  po_date: string;
  status: string;
  supplier: { id: number; supplier_name: string } | null;
  commodity: { id: number; name: string } | null;
  total_bags: number;
  ordered_total: number;
  dispatched_total: number;
  received_total: number;
  pending_ack_total: number;
  remaining_total: number;
  line_items: LineItem[];
}

const PO_STATUS_LABEL: Record<string, { label: string; cls: string }> = {
  draft:            { label: 'Draft',         cls: 'bg-yellow-50 text-yellow-700 border-yellow-300' },
  submitted:        { label: 'Submitted',     cls: 'bg-blue-50 text-blue-700 border-blue-300' },
  dm_verified:      { label: 'DM Verified',   cls: 'bg-orange-50 text-orange-700 border-orange-300' },
  manager_approved: { label: 'Approved',      cls: 'bg-emerald-50 text-emerald-700 border-emerald-300' },
  fulfilled:        { label: 'Fulfilled',     cls: 'bg-purple-50 text-purple-700 border-purple-300' },
  rejected:         { label: 'Rejected',      cls: 'bg-rose-50 text-rose-700 border-rose-300' },
  closed:           { label: 'Closed',        cls: 'bg-gray-100 text-gray-600 border-gray-300' },
};

const CONDITIONS = [
  'Good Condition (New B-twill)',
  'Good Condition (Once-used Sound B-twill)',
  'Good Condition (HDPE)',
  'Damaged / Torn',
  'Mixed',
];

interface EditState {
  consignmentId: number;
  seasonId: number;
  bags_supplied: number;
  bags_utilized: number;
  bags_returned: number;
  condition_society: string;
}

export const SocietyDashboard: React.FC = () => {
  const { user } = useAuth();
  const [seasonId, setSeasonId] = useState<number | null>(null);
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [commodities, setCommodities] = useState<any[]>([]);
  const [pos, setPos] = useState<PoCard[]>([]);
  // Filters
  const [commodityFilter, setCommodityFilter] = useState<number>(0);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [actionFilter, setActionFilter] = useState<'all' | 'awaiting_dm' | 'rejected_correct' | 'ready_to_record' | 'pending_submit' | 'in_review' | 'closed'>('all');
  const [search, setSearch] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const [edit, setEdit] = useState<EditState | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [act, lst, com] = await Promise.all([
          seasonsAPI.active().catch(() => ({ data: null as any })),
          seasonsAPI.list().catch(() => ({ data: [] as Season[] })),
          commoditiesAPI.list().catch(() => ({ data: [] as any[] })),
        ]);
        const list = Array.isArray(lst.data) ? lst.data : [];
        setSeasons(list);
        setCommodities(Array.isArray(com.data) ? com.data : []);
        const sid = act.data?.id || list.find((s) => s.is_active)?.id || list[0]?.id || 0;
        if (sid) setSeasonId(sid);
        else setLoading(false);
      } catch { setLoading(false); }
    })();
  }, []);

  const load = useCallback(async (sid: number) => {
    setLoading(true); setErr(null);
    try {
      const { data } = await gunnyUtilizationAPI.listByPo(sid);
      setPos(data as PoCard[]);
      // Auto-expand POs that already have consignments — those are the ones
      // the society can act on right now.
      const next = new Set<number>();
      (data as PoCard[]).forEach((p) => {
        if (p.line_items.some((it) => it.consignments.length > 0)) next.add(p.id);
      });
      setExpanded(next);
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Failed to load consignments');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (seasonId) load(seasonId); }, [seasonId, load]);

  const summary = useMemo(() => {
    let count = 0, supplied = 0, utilized = 0, returned = 0;
    for (const p of pos) {
      for (const it of p.line_items) {
        for (const c of it.consignments) {
          count += 1;
          supplied += c.bags_supplied;
          utilized += c.bags_utilized;
          returned += c.bags_returned;
        }
      }
    }
    return { count, supplied, utilized, returned };
  }, [pos]);

  const toggle = (id: number) => {
    setExpanded((cur) => { const n = new Set(cur); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const openEditor = (c: Consignment) => {
    setEdit({
      consignmentId: c.id,
      seasonId: seasonId!,
      bags_supplied: c.bags_supplied,
      bags_utilized: c.bags_utilized,
      bags_returned: c.bags_returned,
      condition_society: '',
    });
  };

  const save = async () => {
    if (!edit) return;
    if (edit.bags_utilized + edit.bags_returned > edit.bags_supplied) {
      setErr(`Utilized + returned (${edit.bags_utilized + edit.bags_returned}) exceeds supplied (${edit.bags_supplied})`);
      return;
    }
    setSaving(true); setErr(null);
    try {
      await gunnyUtilizationAPI.societyConfirm(edit.seasonId, edit.consignmentId, {
        bags_utilized: edit.bags_utilized,
        bags_returned: edit.bags_returned,
        condition_society: edit.condition_society || undefined,
      });
      setFlash('Saved.');
      setEdit(null);
      if (seasonId) await load(seasonId);
      setTimeout(() => setFlash(null), 2500);
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const correctCount = async (c: Consignment) => {
    const v = window.prompt(`DM rejected the bags-supplied count.\n\nReason: ${c.receipt_rejection_reason || '—'}\n\nCorrected count:`, String(c.bags_supplied));
    if (!v) return;
    const n = Number(v);
    if (!(n > 0)) { setErr('Enter a positive number'); return; }
    setBusy(c.id);
    try {
      if (!seasonId) return;
      await gunnyUtilizationAPI.correctReceipt(seasonId, c.id, { bags_supplied: n });
      setFlash(`Corrected to ${n}. Awaiting DM verification.`);
      await load(seasonId);
      setTimeout(() => setFlash(null), 3000);
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Correction failed');
    } finally { setBusy(null); }
  };

  const submitConsignment = async (c: Consignment) => {
    if (!window.confirm('Submit this consignment for DM verification?')) return;
    setBusy(c.id);
    try {
      if (!seasonId) return;
      await gunnyUtilizationAPI.submit(seasonId, c.id);
      setFlash('Submitted.');
      await load(seasonId);
      setTimeout(() => setFlash(null), 2500);
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Submit failed');
    } finally { setBusy(null); }
  };

  // Filter pipeline: PO status + commodity + free-text + action-required.
  const filteredPos = useMemo(() => {
    const s = search.trim().toLowerCase();
    return pos.filter((p) => {
      if (statusFilter !== 'all' && p.status !== statusFilter) return false;
      if (commodityFilter > 0) {
        const has = p.commodity?.id === commodityFilter
          || p.line_items.some((it) => it.commodity_id === commodityFilter);
        if (!has) return false;
      }
      if (s) {
        const blob = `${p.po_number} ${p.supplier?.supplier_name || ''} ${p.line_items.map((it) => `${it.bag_type} ${it.commodity_name || ''}`).join(' ')}`.toLowerCase();
        if (!blob.includes(s)) return false;
      }
      if (actionFilter !== 'all') {
        // Compute per-PO action need from its consignments.
        const consigns = p.line_items.flatMap((it) => it.consignments);
        const needsAwaitingDm = (c: Consignment) => c.status === 'draft' && !c.receipt_verified_at && !c.receipt_rejected_at;
        const needsCorrect    = (c: Consignment) => c.status === 'draft' && !!c.receipt_rejected_at;
        const readyToRecord   = (c: Consignment) => c.status === 'draft' && !!c.receipt_verified_at && (c.bags_utilized + c.bags_returned === 0);
        const pendingSubmit   = (c: Consignment) => c.status === 'draft' && !!c.receipt_verified_at && (c.bags_utilized + c.bags_returned > 0);
        const inReview        = (c: Consignment) => c.status === 'submitted' || c.status === 'dm_verified';
        const closedFinal     = (c: Consignment) => c.status === 'manager_approved' || c.status === 'rejected';
        const test = (
          actionFilter === 'awaiting_dm'      ? needsAwaitingDm :
          actionFilter === 'rejected_correct' ? needsCorrect :
          actionFilter === 'ready_to_record'  ? readyToRecord :
          actionFilter === 'pending_submit'   ? pendingSubmit :
          actionFilter === 'in_review'        ? inReview :
          /* closed */                          closedFinal
        );
        if (!consigns.some(test)) return false;
      }
      return true;
    });
  }, [pos, statusFilter, commodityFilter, search, actionFilter]);

  const { page, pageSize, totalPages, totalItems, pagedItems, setPage, setPageSize } =
    usePagination(filteredPos, 10);

  if (loading && pos.length === 0) {
    return <div className="max-w-7xl mx-auto flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-amber-600 animate-spin" /><span className="ml-2 text-gray-500">Loading…</span></div>;
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">My Consignments</h1>
          <p className="text-sm text-gray-600 mt-1">
            Bags supplied to <span className="font-medium">{user?.name}</span> grouped by Purchase Order. For each consignment,
            click <span className="font-medium">Record bags</span> to enter how many bags you used and how many were returned,
            then <span className="font-medium">Submit</span> for DM review.
          </p>
        </div>
        <select value={seasonId || 0} onChange={(e) => setSeasonId(Number(e.target.value) || null)}
          className="px-3 py-1.5 text-sm border rounded bg-white">
          {seasons.map((s) => (<option key={s.id} value={s.id}>{s.season_name}{s.is_active ? ' (active)' : ''}</option>))}
        </select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Tile icon={<Hash className="w-4 h-4" />} label="Consignments" value={summary.count.toLocaleString()} accent="emerald" />
        <Tile icon={<Package className="w-4 h-4" />} label="Bags supplied" value={summary.supplied.toLocaleString()} accent="emerald" />
        <Tile icon={<CheckCircle className="w-4 h-4" />} label="Bags utilized" value={summary.utilized.toLocaleString()} accent="blue" />
        <Tile icon={<RotateCcw className="w-4 h-4" />} label="Bags returned" value={summary.returned.toLocaleString()} accent="amber" />
      </div>

      {/* Filters */}
      <div className="mb-4 bg-white rounded-xl border border-gray-200 p-3 flex flex-wrap gap-2 items-center">
        <select value={commodityFilter} onChange={(e) => setCommodityFilter(parseInt(e.target.value) || 0)}
          className="px-3 py-1.5 text-sm border rounded bg-white">
          <option value={0}>All commodities</option>
          {commodities.map((c: any) => (<option key={c.id} value={c.id}>{c.name}</option>))}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-1.5 text-sm border rounded bg-white">
          <option value="all">All PO statuses</option>
          <option value="draft">Draft</option>
          <option value="submitted">Submitted</option>
          <option value="manager_approved">Approved</option>
          <option value="fulfilled">Fulfilled</option>
          <option value="closed">Closed</option>
          <option value="rejected">Rejected</option>
        </select>
        <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value as any)}
          className="px-3 py-1.5 text-sm border rounded bg-white">
          <option value="all">All consignments</option>
          <option value="awaiting_dm">Awaiting DM verify</option>
          <option value="rejected_correct">Need correction (DM rejected)</option>
          <option value="ready_to_record">Ready to record bags</option>
          <option value="pending_submit">Recorded, pending submit</option>
          <option value="in_review">In review (submitted / DM-verified)</option>
          <option value="closed">Closed (approved / rejected)</option>
        </select>
        <input type="text" placeholder="Search PO no / supplier / bag…" value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="px-3 py-1.5 text-sm border rounded flex-1 min-w-[180px]" />
        <span className="text-xs text-gray-500">{filteredPos.length} of {pos.length} POs</span>
      </div>

      {err && <div className="mb-3 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm"><AlertCircle className="w-4 h-4" /> {err}</div>}
      {flash && <div className="mb-3 flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded text-green-700 text-sm"><CheckCircle className="w-4 h-4" /> {flash}</div>}

      {pos.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-500">
          No bags have been ordered for your PACS in this season yet.
        </div>
      ) : filteredPos.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-500">
          No POs match the current filters.
        </div>
      ) : (
        <div className="space-y-3">
          {pagedItems.map((p) => {
            const cfg = PO_STATUS_LABEL[p.status] || { label: p.status, cls: 'bg-gray-100 text-gray-600 border-gray-300' };
            const isOpen = expanded.has(p.id);
            const dispatchedPct = p.ordered_total > 0 ? Math.round((p.dispatched_total / p.ordered_total) * 100) : 0;
            const receivedPct = p.ordered_total > 0 ? Math.round((p.received_total / p.ordered_total) * 100) : 0;
            const commodityChip = p.commodity?.name
              ? <span className="inline-block px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded text-xs">{p.commodity.name}</span>
              : (() => {
                  const names = Array.from(new Set(p.line_items.map((it) => it.commodity_name).filter(Boolean))) as string[];
                  if (names.length === 1) return <span className="inline-block px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded text-xs">{names[0]}</span>;
                  if (names.length > 1) return <span title={names.join(' · ')} className="inline-block px-2 py-0.5 bg-amber-50 text-amber-700 rounded text-xs">mixed ({names.length})</span>;
                  return <span className="text-xs text-gray-400">—</span>;
                })();

            return (
              <div key={p.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <button onClick={() => toggle(p.id)} className="w-full text-left px-5 py-3 flex items-center gap-3 hover:bg-gray-50">
                  {isOpen ? <ChevronDown className="w-4 h-4 text-gray-500 shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-500 shrink-0" />}
                  <FileText className="w-4 h-4 text-amber-600 shrink-0" />
                  <span className="font-mono font-semibold text-sm">{p.po_number}</span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${cfg.cls}`}>{cfg.label}</span>
                  {commodityChip}
                  <span className="text-xs text-gray-500">{p.supplier?.supplier_name || '—'}</span>
                  <span className="ml-auto text-xs text-gray-500 tabular-nums">{p.po_date}</span>
                </button>

                <div className="px-5 pb-3">
                  <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-xs text-gray-600">
                    <span><span className="text-gray-400">Ordered for me</span> <span className="font-medium tabular-nums text-gray-900">{p.ordered_total.toLocaleString()}</span></span>
                    <span><span className="text-gray-400">Dispatched</span> <span className="font-medium tabular-nums text-gray-900">{p.dispatched_total.toLocaleString()}</span> <span className="text-gray-400">({dispatchedPct}%)</span></span>
                    <span><span className="text-gray-400">Received</span> <span className="font-medium tabular-nums text-emerald-700">{p.received_total.toLocaleString()}</span> <span className="text-gray-400">({receivedPct}%)</span></span>
                    <span><span className="text-gray-400">Remaining</span> <span className={`font-medium tabular-nums ${p.remaining_total === 0 ? 'text-emerald-700' : 'text-amber-700'}`}>{p.remaining_total.toLocaleString()}</span></span>
                  </div>
                  <div className="mt-2 w-full h-2 bg-gray-100 rounded overflow-hidden flex">
                    <div className="bg-emerald-500 h-full" style={{ width: `${receivedPct}%` }} />
                    <div className="bg-amber-400 h-full" style={{ width: `${p.ordered_total > 0 ? Math.round((p.pending_ack_total / p.ordered_total) * 100) : 0}%` }} />
                  </div>
                </div>

                {isOpen && (
                  <div className="border-t bg-gray-50/40 px-5 py-3 space-y-3">
                    {p.line_items.map((it) => {
                      const itDispPct = it.ordered > 0 ? Math.round((it.dispatched / it.ordered) * 100) : 0;
                      const itRecvPct = it.ordered > 0 ? Math.round((it.received / it.ordered) * 100) : 0;
                      return (
                        <div key={it.po_item_id} className="bg-white border border-gray-200 rounded-lg p-3">
                          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-1">
                            {it.commodity_name && <span className="inline-block px-1.5 py-0.5 text-[10px] bg-emerald-50 text-emerald-700 rounded">{it.commodity_name}</span>}
                            <span className="text-sm font-medium text-gray-900">{bagTypeLabel(it.bag_type)}</span>
                            {it.specification && <span className="text-xs text-gray-500">· {it.specification}</span>}
                            {it.delivery_godown_name && <span className="text-[10px] text-gray-500">→ {it.delivery_godown_name}</span>}
                          </div>
                          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xs text-gray-600 mb-2">
                            <span><span className="text-gray-400">Ordered</span> <span className="tabular-nums font-medium text-gray-900">{it.ordered.toLocaleString()}</span></span>
                            <span><span className="text-gray-400">Dispatched</span> <span className="tabular-nums">{it.dispatched.toLocaleString()}</span> <span className="text-gray-400">({itDispPct}%)</span></span>
                            <span><span className="text-gray-400">Received</span> <span className="tabular-nums text-emerald-700">{it.received.toLocaleString()}</span> <span className="text-gray-400">({itRecvPct}%)</span></span>
                            <span><span className="text-gray-400">Remaining</span> <span className={`tabular-nums ${it.remaining === 0 ? 'text-emerald-700' : 'text-amber-700'}`}>{it.remaining.toLocaleString()}</span></span>
                          </div>

                          {it.consignments.length === 0 ? (
                            <div className="text-[11px] text-gray-500 italic">No consignment yet — acknowledge a delivery on the Incoming Lots page first.</div>
                          ) : (
                            <div className="border-t pt-2">
                              <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">My consignments for this lot</div>
                              <table className="min-w-full text-xs">
                                <thead>
                                  <tr className="text-left text-gray-500">
                                    <th className="pr-3 py-1">#</th>
                                    <th className="pr-3 py-1">Status</th>
                                    <th className="pr-3 py-1">DM</th>
                                    <th className="pr-3 py-1 text-right">Supplied</th>
                                    <th className="pr-3 py-1 text-right">Utilized</th>
                                    <th className="pr-3 py-1 text-right">Returned</th>
                                    <th className="pr-3 py-1 text-right">Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {it.consignments.map((c) => {
                                    const loadingRow = busy === c.id;
                                    const draft = c.status === 'draft';
                                    const submitted = c.status === 'submitted';
                                    const receiptVerified = !!c.receipt_verified_at;
                                    const receiptRejected = !!c.receipt_rejected_at;
                                    return (
                                      <tr key={c.id} className="border-t border-gray-100 align-middle">
                                        <td className="pr-3 py-1.5 text-gray-500">#{c.id}</td>
                                        <td className="pr-3 py-1.5">
                                          <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                            draft ? 'bg-yellow-50 text-yellow-700' :
                                            submitted ? 'bg-blue-50 text-blue-700' :
                                            c.status === 'dm_verified' ? 'bg-orange-50 text-orange-700' :
                                            c.status === 'manager_approved' ? 'bg-emerald-50 text-emerald-700' :
                                            c.status === 'rejected' ? 'bg-rose-50 text-rose-700' : 'bg-gray-100 text-gray-500'
                                          }`}>{c.status.replace(/_/g, ' ')}</span>
                                        </td>
                                        <td className="pr-3 py-1.5">
                                          {draft && (
                                            receiptVerified ? <span className="text-emerald-700 text-[10px] flex items-center gap-0.5"><ShieldCheck className="w-2.5 h-2.5" /> verified</span>
                                            : receiptRejected ? <span title={c.receipt_rejection_reason || ''} className="text-rose-600 text-[10px]">rejected</span>
                                            : <span className="text-amber-600 text-[10px]">awaiting DM</span>
                                          )}
                                        </td>
                                        <td className="pr-3 py-1.5 text-right tabular-nums">{c.bags_supplied.toLocaleString()}</td>
                                        <td className="pr-3 py-1.5 text-right tabular-nums">{c.bags_utilized.toLocaleString()}</td>
                                        <td className="pr-3 py-1.5 text-right tabular-nums">{c.bags_returned.toLocaleString()}</td>
                                        <td className="pr-3 py-1.5 text-right whitespace-nowrap">
                                          {receiptRejected && draft && (
                                            <button onClick={() => correctCount(c)} disabled={loadingRow}
                                              className="inline-flex items-center gap-0.5 px-2 py-0.5 text-[10px] bg-amber-600 hover:bg-amber-700 text-white rounded mr-1 disabled:opacity-50"
                                              title="Correct the bags-supplied count and resubmit to DM">
                                              <Pencil className="w-3 h-3" /> Correct count
                                            </button>
                                          )}
                                          {(draft || submitted) && (
                                            <button onClick={() => openEditor(c)}
                                              disabled={draft && !receiptVerified}
                                              title={draft && !receiptVerified ? 'DM must verify the receipt first' : ''}
                                              className="inline-flex items-center gap-0.5 px-2 py-0.5 text-[10px] bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded mr-1">
                                              <Pencil className="w-3 h-3" /> Record bags
                                            </button>
                                          )}
                                          {draft && receiptVerified && (c.bags_utilized + c.bags_returned > 0) && (
                                            <button onClick={() => submitConsignment(c)} disabled={loadingRow}
                                              className="inline-flex items-center gap-0.5 px-2 py-0.5 text-[10px] bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-50">
                                              Submit
                                            </button>
                                          )}
                                          {!draft && !submitted && <span className="text-[10px] text-gray-400">locked</span>}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          {totalPages > 1 && (
            <div className="bg-white rounded-xl border border-gray-200 p-3">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
                totalItems={totalItems}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
                itemLabel="POs"
              />
            </div>
          )}
        </div>
      )}

      {/* Record bags modal */}
      {edit && (() => {
        const remaining = edit.bags_supplied - edit.bags_utilized - edit.bags_returned;
        return (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
              <div className="px-6 py-4 border-b">
                <h2 className="font-semibold text-gray-900">Record bags used / returned</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Out of <span className="font-medium">{edit.bags_supplied.toLocaleString()}</span> bags supplied, enter how
                  many you used and how many were returned. Utilized + returned cannot exceed supplied.
                </p>
              </div>
              <div className="px-6 py-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bags utilized</label>
                  <input type="number" min={0} max={edit.bags_supplied} value={edit.bags_utilized}
                    onChange={(e) => setEdit({ ...edit, bags_utilized: Number(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border rounded text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bags returned</label>
                  <input type="number" min={0} max={edit.bags_supplied} value={edit.bags_returned}
                    onChange={(e) => setEdit({ ...edit, bags_returned: Number(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border rounded text-sm" />
                  <p className="text-xs text-gray-500 mt-1">
                    {edit.bags_returned > 0
                      ? `${edit.bags_returned.toLocaleString()} bag(s) will be sent back to the supplier. The DM tracks the rest — you don't need to do anything else.`
                      : 'Leave at 0 if no bags need to be returned.'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Condition (as received)</label>
                  <select value={edit.condition_society}
                    onChange={(e) => setEdit({ ...edit, condition_society: e.target.value })}
                    className="w-full px-3 py-2 border rounded text-sm bg-white">
                    <option value="">Select a condition…</option>
                    {CONDITIONS.map((c) => (<option key={c} value={c}>{c}</option>))}
                  </select>
                </div>
                <div className={`text-xs ${remaining < 0 ? 'text-rose-700' : 'text-gray-600'}`}>
                  Unaccounted (supplied − utilized − returned): <span className="font-medium tabular-nums">{remaining.toLocaleString()}</span>
                  {remaining < 0 && ' — over the supplied count'}
                </div>
              </div>
              <div className="px-6 py-4 border-t flex justify-end gap-2 bg-gray-50">
                <button onClick={() => setEdit(null)} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded">Cancel</button>
                <button onClick={save} disabled={saving || remaining < 0}
                  className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded">
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

const Tile: React.FC<{ icon: React.ReactNode; label: string; value: string; accent: string }> = ({ icon, label, value, accent }) => {
  const cls = ({
    emerald: 'border-emerald-200 bg-emerald-50',
    blue: 'border-blue-200 bg-blue-50',
    amber: 'border-amber-200 bg-amber-50',
    rose: 'border-rose-200 bg-rose-50',
    gray: 'border-gray-200 bg-gray-50',
  } as Record<string, string>)[accent] || 'border-gray-200 bg-gray-50';
  return (
    <div className={`p-3 rounded-lg border ${cls}`}>
      <div className="flex items-center gap-2 text-xs text-gray-600 mb-1">{icon}{label}</div>
      <div className="text-xl font-semibold tabular-nums text-gray-900">{value}</div>
    </div>
  );
};

// Re-export to suppress unused-import warnings from the lucide-react cleanup pass.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _icons = [XCircle];

export default SocietyDashboard;
